/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { CircularProgress, Paper, Theme, TextField } from '@mui/material';
import LegacyGrid from './legacy/LegacyGrid';
import { withStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { categories, Category, CategoryType } from '../common/Categories';
import { checkUserAuthorized, getDateDiff } from '../common/common';
import { dashboardRoute } from '../common/constants';
import { LicenseData } from '../common/LicenseModel';
import { loadFromLocalStorage, saveToLocalStorage } from '../common/localStorage';
import { ServiceCaseData } from '../common/ServiceCaseModel';
import { actionCreators } from '../store/Dashboard';
import { history } from '../store/history';
import DataTable from './DataTable';
import DoughnutChart from './DoughnutChart';
import GroupedColumnChart from './GroupedColumnChart';
import LicenseDoughnutChart from './LicenseDoughnutChart';
import MyTab from './MyTab';
import NewUserDialog from './NewUserDialog';
import './styles.css';

const styles = (theme: Theme) => ({
    root: {
        flexGrow: 1,
        marginBottom: 24,
        backgroundColor: 'inherit'
    },
    progress: {
        margin: ((theme && (theme as any).spacing) ? (theme as any).spacing(2) : 16)
    },
    columnChart: {
        width: '70%'
    },
    doughnutChart: {
        width: '30%',
        right: '10px'
    },
    chartPaper: {
        padding: '10px',
        margin: '10px'
    },
    message: {
        borderRadius: '0px',
        textAlign: 'center' as const,
        padding: '20px',
        marginTop: '20px'
    },
    progressWrap: {
        textAlign: 'center' as const,
        marginTop: '20vh'
    },
    grid: {
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    datatable: {
        width: '100%',
        padding: '12px',
        backgroundColor: 'transparent'
    }
});

interface Props {
    theme: Theme;
    classes?: any;
    requestChartData: Function;
    chartData: any;
    isLoading: boolean;
    error: any;
    history?: any;
    totalCount: any;
    isChartLoading: boolean;
    location?: any;
    setDialogOpen: Function;
}

class Dashboard extends React.Component<Props, {}> {
    enabledModules: any;

    state = {
        startDate: new Date(new Date(new Date().setDate(new Date().getDate() - 7)).setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(23, 59, 0, 0)),
        selectedCategoryId: -1,
        isUnauthorized: false,
        isNewUser: false,
        newUserMessage: ''
    };

    filters: any;

    constructor(props: Props | Readonly<Props>) {
        super(props);
        this.enabledModules = checkUserAuthorized();
    }

    handleTabChange(categoryId: number) {
        if (categoryId !== undefined) {
            this.setState(
                {
                    selectedCategoryId: categoryId,
                    startDate: categoryId === 5 ? new Date(new Date(new Date()
                        .setDate(new Date().getDate() - 6)).setHours(0, 0, 0, 0)) : new Date(new Date(new Date().setDate(new Date().getDate() - 29)).setHours(0, 0, 0, 0)),
                    endDate: new Date(new Date().setHours(23, 59, 0, 0))
                }
            );
            saveToLocalStorage(categoryId.toString(), 'SelectedTab');
        }
    }

    handleStartDateChange = (value: any, _context?: any) => {
        const date: Moment | null = value as Moment | null;
        if (!date || date.toDate() > this.state.endDate) return;
        this.setState({ startDate: date.toDate() }, () => {
            this.loadData();
        });
    };

    handleEndDateChange = (value: any, _context?: any) => {
        const date: Moment | null = value as Moment | null;
        if (!date || date.toDate() < this.state.startDate) return;
        this.setState({ endDate: date.toDate() }, () => {
            this.loadData();
        });
    };


    handleOk = () => {
        this.setState({
            isNewUser: false,
            newUserMessage: ''
        });
    };

    componentDidMount() {
        if (this.enabledModules == null) {
            return;
        }

        if (this.props.location !== undefined && this.props.location.state !== undefined) {
            const locationState = this.props.location.state;
            if (locationState.isNewUser !== undefined && locationState.newUserMessage !== undefined) {
                this.setState({
                    isNewUser: locationState.isNewUser,
                    newUserMessage: locationState.newUserMessage
                });
                history.replace({
                    pathname: dashboardRoute
                });
            }
        }

        let categoryId: number;
        if (loadFromLocalStorage('SelectedTab') == null) {
            if (this.enabledModules !== undefined && this.enabledModules.length !== 0) {
                const category = categories.find((category: Category) => {
                    return this.enabledModules.find((module: any) => {
                        return module.id - 1 === category.id && category.type === CategoryType.ServiceCase;
                    }) !== undefined;
                });
                categoryId = category === undefined ? categories[0].id : category.id;
                this.setState({
                    selectedCategoryId: categoryId,
                    startDate: categoryId === 5 ? new Date(new Date(new Date().setDate(new Date().getDate() - 7)).setHours(0, 0, 0, 0)) :
                        new Date(new Date(new Date().setDate(new Date().getDate() - 30)).setHours(0, 0, 0, 0)),
                }, () => {
                    this.loadData();
                });
            }
            else {
                this.setState({ isUnauthorized: true });
            }
        }
        else {
            categoryId = parseInt(loadFromLocalStorage('SelectedTab'));
            this.setState({
                selectedCategoryId: categoryId !== 4 ? categoryId : 0,
                startDate: categoryId === 5 ? new Date(new Date(new Date().setDate(new Date().getDate() - 7)).setHours(0, 0, 0, 0)) :
                    new Date(new Date(new Date().setDate(new Date().getDate() - 30)).setHours(0, 0, 0, 0)),
            }, () => {
                this.loadData();
            });
        }
        const searchParams = new URLSearchParams(window.location.search);
        const origin = searchParams.get("origin");
        if (window.location.pathname.includes("dashboard") && origin === "Email") {
            this.props.setDialogOpen(true);
        }
    }

    componentDidUpdate(prevProps: any, prevState: any, snapshot: any) {
        if (prevState.startDate.toDateString() === this.state.startDate.toDateString() &&
            prevState.endDate.toDateString() === this.state.endDate.toDateString() &&
            prevState.selectedCategoryId === this.state.selectedCategoryId) {
            return;
        }
        this.loadData();
    }

    loadData() {
        this.props.requestChartData(
            this.state.selectedCategoryId,
            this.state.startDate,
            this.state.endDate
        );
    }

    refreshChart(filters: any) {
        if (this.state.selectedCategoryId === 4 || (this.state.selectedCategoryId === 5 && ((filters.length === 1 && filters[0].filterProp === 'company') || filters.length === 0))) {
            this.filters = filters;
            this.props.requestChartData(
                this.state.selectedCategoryId,
                this.state.startDate,
                this.state.endDate,
                filters.length >= 1 ? { filters } : undefined
            );
        }
    }

    render() {
        const { classes } = this.props;
        const selectedCategory: Category | undefined = categories.find(
            (category: Category) => {
                return category.id === this.state.selectedCategoryId;
            }
        );
        const isDisabled = selectedCategory !== undefined ? selectedCategory.isDateDisabled : false;
        let errorFetchingModules = false;
        const enabledModules: Array<any> = loadFromLocalStorage('Enabled Modules');
        let filteredCategories: Array<Category> = [];
        if (enabledModules != null && enabledModules.length !== 0) {
            if (enabledModules[0] !== -99) {
                filteredCategories = categories.filter((category: Category) => {
                    return enabledModules.find((module: any) => {
                        return module.id - 1 === category.id && category.type === CategoryType.ServiceCase;
                    }) !== undefined;
                });
            }
            else {
                errorFetchingModules = true;
            }
        }
        return (
            (!this.state.isUnauthorized && !errorFetchingModules) ?
                (
                    <div className={classes.root}>
                        {this.state.isNewUser &&
                            <NewUserDialog dialogContent={this.state.newUserMessage} handleOk={this.handleOk} />
                        }
                        {/*Tab Control*/}
                        <LegacyGrid container spacing={32} style={{ marginBottom: 8 }}>
                            <LegacyGrid item>
                                <MyTab
                                    categories={filteredCategories}
                                    selectedTab={this.state.selectedCategoryId}
                                    onTabChange={this.handleTabChange.bind(this)}
                                />
                            </LegacyGrid>
                        </LegacyGrid>
                        {/*Date Control*/}
                        {!isDisabled && (
                            <LegacyGrid container spacing={40} justifyContent={'flex-start'} style={{ marginTop: 8 }}>
                                <LegacyGrid item={true} style={{ marginRight: 32, marginLeft: 20 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <MobileDatePicker
                                            label="Start Date"
                                            value={this.state.startDate ? moment(this.state.startDate) : null}
                                            onChange={this.handleStartDateChange}
                                            disableFuture
                                            disabled={!navigator.onLine}
                                            format="MMMM D YYYY"
                                            closeOnSelect
                                            slotProps={{
                                                textField: {
                                                    sx: { width: 180 },
                                                    variant: "standard",
                                                    className: 'dashboard-date-field',
                                                    InputProps: {
                                                        style: { backgroundColor: 'transparent' }
                                                    }
                                                },
                                                openPickerButton: {
                                                    className: 'dashboard-date-button'
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </LegacyGrid>
                                <LegacyGrid item={true} style={{ marginLeft: 120 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <MobileDatePicker
                                            label="End Date"
                                            value={this.state.endDate ? moment(this.state.endDate) : null}
                                            onChange={this.handleEndDateChange}
                                            disableFuture
                                            disabled={!navigator.onLine}
                                            format="MMMM D YYYY"
                                            closeOnSelect
                                            slotProps={{
                                                textField: {
                                                    sx: { width: 180 },
                                                    variant: "standard",
                                                    className: 'dashboard-date-field',
                                                    InputProps: {
                                                        style: { backgroundColor: 'transparent' }
                                                    }
                                                },
                                                openPickerButton: {
                                                    className: 'dashboard-date-button'
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </LegacyGrid>
                            </LegacyGrid>
                        )}
                        {/*Chart Control*/}
                        {this.props.isLoading ?
                            (
                                <div className={classes.progressWrap}>
                                    <CircularProgress className={classes.progress} color="secondary" />
                                </div>
                            ) :
                            this.props.chartData === undefined || this.props.error !== undefined ?
                                (
                                    <Paper className={classes.message}>
                                        Oops ! An unexpected error occurred.Please try again later
                                    </Paper>
                                ) : this.props.totalCount === 0 ?
                                    (
                                        <Paper className={classes.message}>No data available</Paper>
                                    ) :
                                    (
                                        <div>
                                            {(selectedCategory !== undefined ?
                                                (((this.props.isChartLoading || this.props.chartData.datasets === undefined || this.props.chartData.xaxisData === undefined)) ||
                                                    (selectedCategory.type === CategoryType.License && (this.props.chartData.groupedDoughnutData === undefined ||
                                                        this.props.chartData.groupedDoughnutLabel === undefined))
                                                ) ?
                                                    <div className={classes.progressWrap}>
                                                        <CircularProgress className={classes.progress} color="secondary" />
                                                    </div> :

                                                    (<LegacyGrid container={true} className={classes.grid}>
                                                        <LegacyGrid item xs={8}>
                                                            <GroupedColumnChart
                                                                datasets={this.props.chartData.datasets}
                                                                xaxisLabel={selectedCategory.xaxisLabel}
                                                                xaxisData={this.props.chartData.xaxisData}
                                                                yaxisLabel={selectedCategory.yaxisLabel}
                                                                MaxBarNum={this.props.chartData.maxBarnum}
                                                                label={selectedCategory.chartLabel}
                                                                category={selectedCategory}
                                                            />
                                                        </LegacyGrid>
                                                        <LegacyGrid item xs={4} style={{ display: 'flex', justifyContent: 'center' }}>
                                                            {(selectedCategory.type === CategoryType.ServiceCase) ?
                                                                <DoughnutChart
                                                                    label={this.props.chartData.doughnutLabel}
                                                                    data={this.props.chartData.doughnutData}
                                                                    imageNames={this.props.chartData.doughnutImageNames}
                                                                    category={selectedCategory}
                                                                />
                                                                :
                                                                <LicenseDoughnutChart
                                                                    labels={this.props.chartData.groupedDoughnutLabel}
                                                                    dataSets={this.props.chartData.groupedDoughnutData}
                                                                    licenseTypeTexts={this.props.chartData.licenseTypeTexts}
                                                                />}
                                                        </LegacyGrid>
                                                    </LegacyGrid>) :
                                                <div />)}
                                        </div>
                                    )}
                        {/*Table Control*/}
                        {(this.props.chartData !== undefined && !this.props.isLoading &&
                            this.props.error === undefined || this.props.isChartLoading) &&
                            (this.state.selectedCategoryId === 4 || (this.state.selectedCategoryId === 5 &&
                                this.filters !== undefined && this.filters.length === 1) ? true : this.props.totalCount !== 0) &&
                            <LegacyGrid container={true} spacing={24}>
                                <LegacyGrid item={true} className={classes.datatable}>
                                    <DataTable
                                        startDate={this.state.startDate}
                                        endDate={this.state.endDate}
                                        selectedCategory={selectedCategory}
                                        refreshChart={this.refreshChart.bind(this)}
                                    />
                                </LegacyGrid>
                            </LegacyGrid>}

                    </div>) :

                <Paper className={classes.message}>
                    {errorFetchingModules ? 'Unable to fetch data.Please try again later' : 'You are not authorized to view the data.'}
                </Paper>

        );
    }
}

function groupBy(
    array: any[],
    f: (item: any) => string[]
) {
    const groups: any = {};
    array.forEach(function (o) {
        const group: string = JSON.stringify(f(o));
        groups[group] = groups[group] || [];
        groups[group].push(o);
    });
    return Object.keys(groups).map(function (group) {
        return groups[group];
    });
}

function getChartData(
    data: any,
    selectedCategory: Category,
    isLoading: boolean
) {
    if (
        data === undefined ||
        selectedCategory === undefined ||
        (data.length === undefined && selectedCategory.id !== 5) ||
        isLoading === true
    ) {
        return undefined;
    }
    let xaxisData: Array<string> = [];
    const yaxisData: Array<number> = [];
    const doughnutLabel: Array<string> = [];
    const doughnutData: Array<number> = [];
    const doughnutImageNames: Array<string> = [];
    const xaxislabels: Array<string> = [];
    let datasets: any = [];
    let maxBarnum = 1;
    if (data.length !== 0) {
        switch (selectedCategory.type) {
            case CategoryType.ServiceCase:
                switch (selectedCategory.id) {
                    case 0:
                    case 1:
                        data.map((x: ServiceCaseData) => {
                            xaxisData.push(x.id);
                            yaxisData.push(x.age);
                        });
                        datasets.push({
                            name: selectedCategory.yaxisLabel,
                            data: yaxisData
                        });
                        break;
                    case 2:
                        var result = groupBy(data, function (item: ServiceCaseData) {
                            return [item.assigneduser.firstname, item.assigneduser.lastname];
                        });
                        result.forEach(function (servicaseByUsers: any) {
                            xaxisData.push(
                                `${servicaseByUsers[0].assigneduser.firstname
                                } ${servicaseByUsers[0].assigneduser.lastname}`
                            );
                            yaxisData.push(servicaseByUsers.length);
                        });
                        datasets.push({
                            name: selectedCategory.yaxisLabel,
                            data: yaxisData
                        });
                        break;
                    case 3:
                        var result = groupBy(data, function (item: ServiceCaseData) {
                            return item.status === 2 && item.closedbyuser != null ?
                                [item.closedbyuser.firstname, item.closedbyuser.lastname] : [item.assigneduser.firstname, item.assigneduser.lastname];
                        });
                        result.forEach(function (servicaseByUsers: any) {
                            if (servicaseByUsers[0].status === 2 && servicaseByUsers[0].closedbyuser != null) {
                                xaxisData.push(
                                    `${servicaseByUsers[0].closedbyuser.firstname
                                    } ${servicaseByUsers[0].closedbyuser.lastname}`
                                );
                            }
                            else {
                                xaxisData.push(
                                    `${servicaseByUsers[0].assigneduser.firstname
                                    } ${servicaseByUsers[0].assigneduser.lastname}`
                                );
                            }

                            yaxisData.push(servicaseByUsers.length);
                        });
                        datasets.push({
                            name: selectedCategory.yaxisLabel,
                            data: yaxisData
                        });
                        break;
                    case 5:
                        const users: Array<string> = [];
                        const openCases: Array<number> = [];
                        const closedCases: Array<number> = [];
                        var results = data.userCaseStatistics.data.allservicecases.data;
                        results.forEach((i: any) => {
                            users.push(`${i.firstName} ${i.lastName}`);
                            openCases.push(i.openCases);
                            closedCases.push(i.closedCases);
                        });

                        xaxisData = users;
                        datasets = [{
                            name: ['Open'],
                            backgroundColor: 'rgb(255,255,255)',
                            borderColor: '#D3D3D3',
                            borderWidth: 1,
                            hoverBackgroundColor: '#ffbe00',
                            data: openCases
                        },
                        {
                            name: ['Closed'],
                            backgroundColor: 'rgb(255,255,255)',
                            borderColor: '#D3D3D3',
                            borderWidth: 1,
                            hoverBackgroundColor: '#ffbe00',
                            data: closedCases
                        }
                        ];
                        maxBarnum = 2;
                        break;
                }
                break;
            case CategoryType.License:
                var result = groupBy(data, function (item: LicenseData) {
                    return item.user != null ? [item.user.firstname, item.user.lastname] : item.company != null ? [item.company.companyname] : [''];
                });
                let maxLength = 1;
                result.forEach(function (licenseByUsers: any) {
                    xaxislabels.push(
                        licenseByUsers[0].user != null
                            ? `${licenseByUsers[0].user.firstname} ${licenseByUsers[0].user.lastname}`
                            : licenseByUsers[0].company != null ? licenseByUsers[0].company.companyname : ''
                    );
                    maxLength = licenseByUsers.length > maxLength ? licenseByUsers.length : maxLength;
                });
                maxLength = maxLength > 10 ? 10 : maxLength;
                const dataSets = [];
                //xaxislabels = xaxislabels.filter((x: any, index: any, array: any) => { if (index < 5) return x; })

                for (let j = 0; j < maxLength; j++) {

                    const dataSet = {
                        label: [],
                        data: [],
                        backgroundColor: [],
                        borderColor: [],
                        hoverBackgroundColor: [],
                    };

                    for (let i = 0; i < xaxislabels.length; i++) {
                        if (result[i][j] !== undefined && result[i][j].subscription !== undefined) {
                            const color = colors[j % colors.length];
                            dataSet.label.push(result[i][j].machinename as never);
                            const expiresin = getDateDiff(new Date(), result[i][j].subscription.length !== 0 ? new Date(result[i][j].subscription[0].enddate + 'Z') : new Date());
                            if (expiresin < 0) {
                                dataSet.backgroundColor.push('#bfbfc6' as never);
                                dataSet.borderColor.push('#bfbfc6' as never);
                                dataSet.hoverBackgroundColor.push('#bfbfc6' as never);
                            }
                            else if (expiresin < 7) {
                                dataSet.backgroundColor.push('#ff8b00' as never);
                                dataSet.borderColor.push('#ff8b00' as never);
                                dataSet.hoverBackgroundColor.push('#ff8b00' as never);
                            }
                            else {
                                dataSet.backgroundColor.push(color as never);
                                dataSet.borderColor.push(color as never);
                                dataSet.hoverBackgroundColor.push(color as never);
                            }
                            dataSet.data.push(Math.abs(expiresin) as never);
                        }
                        else {
                            dataSet.label.push(' ' as never);
                            dataSet.data.push(0 as never);
                            dataSet.backgroundColor.push('#bfbfc6' as never);
                            dataSet.borderColor.push('#bfbfc6' as never);
                            dataSet.hoverBackgroundColor.push('#bfbfc6' as never);
                        }
                    }
                    dataSets.push(dataSet);
                }

                //Doughnut chart
                const companyNames: Array<string> = [];
                const companyLicenseDataSet: Array<number> = [];
                const companyLicenseColors: Array<string> = [];
                const licenseTypeLabels: Array<string> = [];
                const licenseTypeTexts: Array<string> = [];
                const licenseByCompanies = groupBy(data, function (item: LicenseData) {
                    return (item.user != null && item.user.company != null) ? [item.user.company.companyname] : item.company != null ? [item.company.companyname] : [''];
                });
                licenseByCompanies.forEach((licenseByCompany: Array<LicenseData>, index: number, array: any) => {
                    companyNames.push(
                        (licenseByCompany[0].user != null && licenseByCompany[0].user.company != null) ? licenseByCompany[0].user.company.companyname
                            : (licenseByCompany[0].company != null) ? licenseByCompany[0].company.companyname : 'Internal Trimble');
                    companyLicenseDataSet.push(licenseByCompany.length);
                    //dataSet2.push(Math.abs(((licenseByCompany.length / licenseByCompanies.length) * 100)))
                    const color = colors[index % colors.length];
                    companyLicenseColors.push(color);
                    const licenseByTypes: any = groupBy(licenseByCompany, function (item: any) {
                        return item.licensetype != null ? [item.licensetype] : [''];
                    });
                    let licenseTypeText = '';
                    licenseByTypes.forEach((licensebytype: any) => {
                        //licenseTypeDataSet.push(licensebytype.length)
                        //licenseTypeColors.push(color)
                        //licenseTypeLabels.push(licensebytype[0].licensetype.typename)
                        licenseTypeText = `${licenseTypeText}${licensebytype.length} ${licensebytype[0].licensetype.typename}\n`;
                    });
                    licenseTypeTexts.push(licenseTypeText);
                });
                const doughnutData = [
                    //{ data: licenseTypeDataSet, backgroundColor: licenseTypeColors, labels: licenseTypeLabels, hoverBackgroundColor: '#ffbe00', hoverBorderColor: '#ffbe00'},
                    { data: companyLicenseDataSet, backgroundColor: companyLicenseColors, labels: companyNames, hoverBackgroundColor: '#ffbe00', hoverBorderColor: '#ffbe00' }];
                return {
                    xaxisData: xaxislabels,
                    yaxisData: ['dummy', 'dummy'],
                    doughnutLabel: ['dummy', 'dummy'],
                    doughnutData: ['dummy', 'dummy'],
                    groupedDoughnutLabel: licenseTypeLabels,
                    groupedDoughnutData: doughnutData,
                    doughnutImageNames: ['dummy', 'dummy'],
                    datasets: dataSets,
                    maxBarnum: maxLength,
                    licenseTypeTexts
                };
        }

        //DoughnutData
        if (selectedCategory.id === 5) {
            const results = data.productStatistics.data.allservicecases.data;
            results.forEach((i: any) => {
                doughnutLabel.push(i.twodproductname);
                doughnutData.push(i.numberOfCases);
                doughnutImageNames.push(i.productLine.replace('/', ''));
            });
        }
        else {
            const result = groupBy(data, function (item: ServiceCaseData) {
                return [item.instrument.productmodel.product.twodproductname != null ?
                    item.instrument.productmodel.product.twodproductname.name : item.instrument.productmodel.product.displayname];
            });
            result.forEach(function (serviceCaseByProductLine: Array<ServiceCaseData>) {
                doughnutLabel.push(
                    serviceCaseByProductLine[0].instrument.productmodel.product.twodproductname != null ?
                        serviceCaseByProductLine[0].instrument.productmodel.product.twodproductname.name : serviceCaseByProductLine[0].instrument.productmodel.product.displayname
                );
                doughnutData.push(serviceCaseByProductLine.length);
                doughnutImageNames.push(
                    serviceCaseByProductLine[0].instrument.productmodel.product.imagename
                );
            });
        }
    }
    return {
        xaxisData,
        yaxisData,
        doughnutLabel,
        doughnutData,
        doughnutImageNames,
        datasets,
        maxBarnum
    };
}

const shades: Array<string> = ['#bfbfc6', '#e2e2e7', '#d0d0d7'];
const colors: Array<string> = ['#CFE1EE', '#DFE9D6', '#FFF2CC', '#FFE8CC', '#F4D1D3'];
const mapStateToProps = (state: any) => ({
    dialogOpen: state.dashboard.dialogOpen,
    state: state.dashboard,
    isLoading: state.dashboard.isLoading,
    chartData: getChartData(
        state.dashboard.data,
        state.dashboard.selectedCategory,
        state.dashboard.isLoading
    ),
    totalCount: state.dashboard.totalCount,
    error: state.dashboard.error,
    isChartLoading: state.dashboard.isChartLoading
});

const mapDispatchtoProps = (dispatch: any) =>
    bindActionCreators(actionCreators, dispatch);

export default connect(
    mapStateToProps,
    mapDispatchtoProps
)(withStyles(styles, { withTheme: true })(Dashboard));
